import {
  calculateDeadlineFromOfferCargoDestination,
  checkProposedDeadlineSatisfied,
} from "@/features/offer-cargo-destination-deadline";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { Box, Container, Stack, Typography } from "@mui/material";
import { Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { CarrierDeliveryProposalCreatePageProps } from "../../model";
import { DestinationsForm } from "./forms/destinations-form";
import { MemoForm } from "./forms/memo-form";
import { PaymentForm } from "./forms/payment-form";
import { VehicleForm } from "./forms/vehicle-form";

export const DeliveryProposalCreatePreview = ({
  draftOfferCargo,
  previewValues,
  onBackToInProgressClick,
  onSubmitClick,
}: CarrierDeliveryProposalCreatePageProps) => {
  const links = [
    { href: "/carrier/cargos?tab=not_proposed", label: "荷物一覧" },
    {
      href: draftOfferCargo.data
        ? `/carrier/cargos/${draftOfferCargo.data.id}`
        : undefined,
      label: "荷物詳細",
    },
    {
      label: "配送提案の新規登録",
    },
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmitClick = useCallback(async () => {
    setIsSubmitting(true);
    await onSubmitClick();
    setIsSubmitting(false);
  }, [onSubmitClick]);

  const areAllProposedDeadlineSatisfied = useMemo(() => {
    const draftOfferCargoData = draftOfferCargo.data;
    if (!draftOfferCargoData || !previewValues) {
      return true;
    }
    for (const [
      i,
      draftOfferCargoDestination,
    ] of draftOfferCargoData.destinations.entries()) {
      const taskContent: "loading" | "transit" | "unloading" =
        i === 0
          ? "loading"
          : i === draftOfferCargoData.destinations.length - 1
            ? "unloading"
            : "transit";
      const previewDestinationValues =
        i === 0
          ? previewValues.loadingDestination
          : i === draftOfferCargoData.destinations.length - 1
            ? previewValues.unloadingDestination
            : previewValues.transitDestinations[i - 1];
      const draftDeadlineValues = calculateDeadlineFromOfferCargoDestination(
        taskContent,
        draftOfferCargoData,
        draftOfferCargoDestination,
      );
      const proposalDeadlineValues = {
        ...previewDestinationValues,
      };
      if (
        !checkProposedDeadlineSatisfied(
          draftDeadlineValues,
          proposalDeadlineValues,
        )
      ) {
        return false;
      }
    }
    return true;
  }, [draftOfferCargo.data, previewValues]);
  if (!previewValues) {
    return (
      <Stack justifyContent="center" alignItems="center">
        <Typography variant="h6" color="error">
          配送提案情報がありません
        </Typography>
      </Stack>
    );
  }

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          配送提案の新規登録
        </Typography>
      </Box>

      <Stack
        bgcolor="grey.50"
        minHeight="600px"
        py={4}
        sx={{
          mb: { xs: 3, sm: 4 },
          mx: { xs: -2, sm: 0 },
        }}
      >
        <Container sx={{ width: { sm: 920 }, px: 0 }}>
          <Stack spacing={2}>
            <DestinationsForm
              draftOfferCargo={draftOfferCargo}
              previewValues={previewValues}
            />
            <BaseDivider />
            <PaymentForm
              draftOfferCargo={draftOfferCargo}
              previewValues={previewValues}
            />
            <BaseDivider />
            <VehicleForm
              draftOfferCargo={draftOfferCargo}
              previewValues={previewValues}
            />
            <BaseDivider />
            <MemoForm
              draftOfferCargo={draftOfferCargo}
              previewValues={previewValues}
            />
          </Stack>
        </Container>
      </Stack>

      <Stack spacing={2} mb={2}>
        <Stack justifyContent="center" alignItems="center" spacing={1}>
          <Typography variant="body2">
            ※ 配送提案すると、荷主に通知されます
          </Typography>
          {!areAllProposedDeadlineSatisfied && (
            <BaseMessageCard
              message="荷物の要件を満たしていない積卸日時が提案されています。このまま提案しますか？"
              status="warning"
              size="medium"
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="center">
          <BaseButton
            LinkComponent={Link}
            variant="outlined"
            color="primary"
            sx={{ width: "96px", fontSize: "14px", fontWeight: 700 }}
            onClick={onBackToInProgressClick}
          >
            修正する
          </BaseButton>
          <BaseButton
            variant="contained"
            color="primary"
            sx={{ width: "240px", fontSize: "14px", fontWeight: 700 }}
            onClick={handleSubmitClick}
            disabled={isSubmitting}
          >
            配送提案する
          </BaseButton>
        </Stack>
      </Stack>
    </Box>
  );
};
